package com.kinoshazam.taste

import com.kinoshazam.model.ServerResult
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.get
import io.ktor.client.request.parameter
import io.ktor.client.request.post
import io.ktor.client.request.setBody

class TasteRepository(
    private val httpClient: HttpClient,
) {

    suspend fun saveSession(request: TasteSessionRequest): TasteSessionDto? {
        return httpClient.post("taste/session") {
            setBody(request)
        }.body<ServerResult<TasteSessionDto?>>().result
    }

    suspend fun latestSession(): TasteSessionDto? {
        return httpClient.get("taste/session/latest").body<ServerResult<TasteSessionDto?>>().result
    }

    suspend fun replaceSeeds(items: List<TasteTitleRef>): List<TasteTitleRef> {
        return httpClient.post("taste/seeds") {
            setBody(TasteSeedsRequest(items))
        }.body<ServerResult<List<TasteTitleRef>?>>().result.orEmpty()
    }

    suspend fun listSeeds(): List<TasteTitleRef> {
        return httpClient.get("taste/seeds").body<ServerResult<List<TasteTitleRef>?>>().result.orEmpty()
    }

    suspend fun seedCandidates(page: Int = 1): List<TasteCatalogItem> {
        return httpClient.get("taste/seed-candidates") {
            parameter("page", page)
        }.body<ServerResult<List<TasteCatalogItem>?>>().result.orEmpty()
    }

    suspend fun addSignal(request: TasteSignalRequest): TasteSignalDto? {
        return httpClient.post("taste/signals") {
            setBody(request)
        }.body<ServerResult<TasteSignalDto?>>().result
    }

    suspend fun profile(): TasteProfileDto? {
        return httpClient.get("taste/profile").body<ServerResult<TasteProfileDto?>>().result
    }

    suspend fun mergeDevice(): TasteMergeResult? {
        return httpClient.post("taste/merge").body<ServerResult<TasteMergeResult?>>().result
    }

    suspend fun pick(exclude: List<TasteTitleRef> = emptyList()): TastePickDto? {
        return httpClient.post("taste/pick") {
            setBody(TastePickRequest(exclude = exclude))
        }.body<ServerResult<TastePickDto?>>().result
    }

    suspend fun createParty(request: TastePartyCreateRequest): TastePartyDto? {
        return httpClient.post("taste/parties") {
            setBody(request)
        }.body<ServerResult<TastePartyDto?>>().result
    }

    suspend fun party(code: String): TastePartyDto? {
        return httpClient.get("taste/parties/${code.trim()}")
            .body<ServerResult<TastePartyDto?>>().result
    }

    suspend fun joinParty(code: String, displayName: String? = null): TastePartyDto? {
        return httpClient.post("taste/parties/${code.trim()}/join") {
            setBody(TastePartyJoinRequest(displayName = displayName))
        }.body<ServerResult<TastePartyDto?>>().result
    }

    suspend fun setPartyReady(code: String, ready: Boolean = true): TastePartyDto? {
        return httpClient.post("taste/parties/${code.trim()}/ready") {
            setBody(TastePartyReadyRequest(ready = ready))
        }.body<ServerResult<TastePartyDto?>>().result
    }

    suspend fun leaveParty(code: String): TastePartyDto? {
        return httpClient.post("taste/parties/${code.trim()}/leave")
            .body<ServerResult<TastePartyDto?>>().result
    }

    suspend fun partyPick(code: String, exclude: List<TasteTitleRef> = emptyList()): TastePartyDto? {
        return httpClient.post("taste/parties/${code.trim()}/pick") {
            setBody(TastePickRequest(exclude = exclude))
        }.body<ServerResult<TastePartyDto?>>().result
    }

    suspend fun partySignal(code: String, request: TasteSignalRequest): TastePartyDto? {
        return httpClient.post("taste/parties/${code.trim()}/signal") {
            setBody(request)
        }.body<ServerResult<TastePartyDto?>>().result
    }

    suspend fun setPartyCapacity(code: String, capacity: Int): TastePartyDto? {
        return httpClient.post("taste/parties/${code.trim()}/settings") {
            setBody(TastePartySettingsRequest(capacity = capacity))
        }.body<ServerResult<TastePartyDto?>>().result
    }

    suspend fun lockPartyPick(code: String): TastePartyDto? {
        return httpClient.post("taste/parties/${code.trim()}/lock")
            .body<ServerResult<TastePartyDto?>>().result
    }
}
